import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { GlassToastContainer } from './GlassToast';
import type { ToastMessage, ToastType } from './GlassToast';

interface ToastContextType {
  showToast: (type: ToastType, message: string, options?: ToastOptions) => string;
  dismissToast: (id: string) => void;
  dismissAll: () => void;
}

export interface ToastOptions {
  duration?: number;
  dismissible?: boolean;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToastContext = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToastContext must be used within a ToastProvider');
  }
  return context;
};

interface ToastProviderProps {
  children: React.ReactNode;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';
  maxToasts?: number;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ 
  children, 
  position = 'top-right', 
  maxToasts = 5 
}) => {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const toastIdCounter = useRef(0);

  const showToast = useCallback((type: ToastType, message: string, options: ToastOptions = {}) => {
    const id = `toast-${++toastIdCounter.current}`;
    
    const newToast: ToastMessage = {
      id,
      type,
      message,
      duration: options.duration || 4000,
      dismissible: options.dismissible !== false
    };

    setToasts(prevToasts => {
      const updatedToasts = [newToast, ...prevToasts];
      // Limit the number of toasts
      return updatedToasts.slice(0, maxToasts);
    });

    return id;
  }, [maxToasts]);

  const dismissToast = useCallback((id: string) => {
    setToasts(prevToasts => prevToasts.filter(toast => toast.id !== id));
  }, []);

  const dismissAll = useCallback(() => {
    setToasts([]);
  }, []);

  const contextValue: ToastContextType = {
    showToast,
    dismissToast,
    dismissAll
  };

  // Register context globally for toast utilities
  React.useEffect(() => {
    (window as any).__glassToastContext = contextValue;
    return () => {
      delete (window as any).__glassToastContext;
    };
  }, [contextValue]);

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <GlassToastContainer 
        toasts={toasts}
        onDismiss={dismissToast}
        position={position}
      />
    </ToastContext.Provider>
  );
};

// Toast utilities - compatible with react-toastify API
class ToastUtilities {
  private static getContext(): ToastContextType {
    // This is a workaround to access context outside of React components
    // We'll store the context reference in a global variable
    const context = (window as any).__glassToastContext;
    if (!context) {
      console.warn('Toast utilities called outside of ToastProvider context');
      return {
        showToast: () => '',
        dismissToast: () => {},
        dismissAll: () => {}
      };
    }
    return context;
  }

  static success(message: string, options?: ToastOptions): string {
    return this.getContext().showToast('success', message, options);
  }

  static error(message: string, options?: ToastOptions): string {
    return this.getContext().showToast('error', message, options);
  }

  static warning(message: string, options?: ToastOptions): string {
    return this.getContext().showToast('warning', message, options);
  }

  static warn(message: string, options?: ToastOptions): string {
    return this.warning(message, options);
  }

  static info(message: string, options?: ToastOptions): string {
    return this.getContext().showToast('info', message, options);
  }

  static dismiss(id?: string): void {
    if (id) {
      this.getContext().dismissToast(id);
    } else {
      this.getContext().dismissAll();
    }
  }
}

// Export the toast object with the same API as react-toastify
export const toast = ToastUtilities;
